import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { performPostAttachment } from "store/attachments/api/attachmentsApi";
import { AppThunk } from "store";
import { submitFormSuccess } from "store/form/formDataSlice";
import { AttachmentsState } from "./types";
import { AttachmentOperationState } from "@entaconsulting/form";

const initialState: AttachmentsState = {
  items: {},
  pendingOperationsCount: 0,
  pendingOperations: false,
  toRemove: {},
};
const getInitialOperationState = (): AttachmentOperationState => ({
  submitOperations: [],
  inProgressCount: 0,
  inErrorCount: 0,
});
const attachmentsSlice = createSlice({
  name: "attachments",
  initialState: initialState,
  reducers: {
    addAtachment(
      state,
      action: PayloadAction<{ fieldName: string; fileName: string }>
    ) {
      const { fieldName, fileName } = action.payload;
      const curState = (state.items[fieldName] =
        state.items[fieldName] || getInitialOperationState());

      //check if there is a pending operation for the same file
      const existingOperation = curState.submitOperations.find(
        (x) => x.fileName === fileName && x.status !== "success"
      );
      if (existingOperation) {
        if (existingOperation.operation === "remove") {
          //transform the remove operation in an uplocad operation
          existingOperation.operation = "upload";
        } else {
          return;
          //if there is another kind of existing operation, add cannot proceed
        }
      } else {
        curState.submitOperations.push({
          fieldName,
          fileName,
          operation: "upload",
          status: "new",
        });
      }
      state.pendingOperationsCount++;
      state.pendingOperations = state.pendingOperationsCount > 0;
    },
    replaceAtachment(
      state,
      action: PayloadAction<{ fieldName: string; fileName: string }>
    ) {
      const { fieldName, fileName } = action.payload;
      const curState = (state.items[fieldName] =
        state.items[fieldName] || getInitialOperationState());
      curState.submitOperations.push({
        fieldName,
        fileName,
        operation: "replace",
        status: "new",
      });
      state.pendingOperationsCount++;
      state.pendingOperations = state.pendingOperationsCount > 0;
    },
    removeAttachment(
      state,
      action: PayloadAction<{ fieldName: string; fileName: string }>
    ) {
      const { fieldName, fileName } = action.payload;
      const curState = (state.items[fieldName] =
        state.items[fieldName] || getInitialOperationState());
      curState.submitOperations.push({
        fieldName: fieldName,
        fileName: fileName,
        operation: "remove",
        status: "new",
      });
    },
    cancelOperation(
      state,
      action: PayloadAction<{ fieldName: string; fileName: string }>
    ) {
      const { fieldName, fileName } = action.payload;
      const curState = state.items[fieldName];
      const operationIndex =
        curState?.submitOperations.findIndex(
          (operation) =>
            operation.fileName === fileName && operation.status !== "success"
        ) ?? -1;
      if (!curState || operationIndex === -1) {
        throw new Error(
          `there is no pending operation to cancel for file '${fileName}'`
        );
      }
      curState.submitOperations.splice(operationIndex, 1);
      state.pendingOperationsCount--;
      state.pendingOperations = state.pendingOperationsCount > 0;
    },
    retryOperation(
      state,
      action: PayloadAction<{ fieldName: string; fileName: string }>
    ) {
      const { fieldName, fileName } = action.payload;
      const curState = state.items[fieldName];
      const operationIndex =
        curState?.submitOperations.findIndex(
          (operation) =>
            operation.fileName === fileName && operation.status === "error"
        ) ?? -1;
      if (!curState || operationIndex === -1) {
        throw new Error(
          `there is no operation to retry for file '${fileName}'`
        );
      }
      curState.submitOperations[operationIndex].status = "new";
      curState.submitOperations[operationIndex].progress = undefined;
      curState.submitOperations[operationIndex].error = undefined;
    },
    submitSuccess(
      state,
      action: PayloadAction<{ fieldName: string; fileName: string }>
    ) {
      const curState = state.items[action.payload.fieldName];
      const idx =
        curState?.submitOperations.findIndex(
          (x) => x.fileName === action.payload.fileName
        ) ?? -1;
      if (curState && idx !== -1) {
        curState.submitOperations[idx].status = "success";
        curState.submitOperations[idx].progress = undefined;
        curState.submitOperations[idx].error = undefined;
        curState.inProgressCount--;

        state.pendingOperationsCount--;
        state.pendingOperations = state.pendingOperationsCount > 0;
      }
    },
    submitFailed(
      state,
      action: PayloadAction<{
        fieldName: string;
        fileName: string;
        error: string;
      }>
    ) {
      const curState = state.items[action.payload.fieldName];
      const idx =
        curState?.submitOperations.findIndex(
          (x) => x.fileName === action.payload.fileName
        ) ?? -1;
      if (curState && idx !== -1) {
        curState.submitOperations[idx].status = "error";
        curState.submitOperations[idx].progress = undefined;
        curState.submitOperations[idx].error = action.payload.error;
        curState.inProgressCount--;
        curState.inErrorCount++;
      }
    },
    submitStart(
      state,
      action: PayloadAction<{
        fieldName: string;
        fileName: string;
      }>
    ) {
      const curState = state.items[action.payload.fieldName];
      const idx =
        curState?.submitOperations.findIndex(
          (x) => x.fileName === action.payload.fileName
        ) ?? -1;
      if (curState && idx !== -1) {
        curState.submitOperations[idx].status = "inProgress";
        curState.inProgressCount++;
      }
    },
    updateProgress(
      state,
      action: PayloadAction<{
        fieldName: string;
        fileName: string;
        progress: number;
      }>
    ) {
      const curState = state.items[action.payload.fieldName];
      const idx =
        curState?.submitOperations.findIndex(
          (x) => x.fileName === action.payload.fileName
        ) ?? -1;
      if (curState && idx !== -1) {
        curState.submitOperations[idx].progress = action.payload.progress;
      }
    },
    purgeOperations(
      state,
      action: PayloadAction<{ fieldName: string; fileNames: string[] }>
    ) {
      const { fieldName, fileNames } = action.payload;
      if (!fileNames.length) return;
      const curState = state.items[fieldName];
      if (!curState) {
        return;
      }
      //only operations in success status can be purged. other should be canceled instead
      fileNames.forEach((fileName) => {
        const operationIndex = curState.submitOperations.findIndex(
          (operation) => operation.fileName === fileName
        );
        if (
          operationIndex >= 0 &&
          curState.submitOperations[operationIndex].status === "success"
        ) {
          curState.submitOperations.splice(operationIndex, 1);
        }
      });
      curState.inErrorCount = curState.submitOperations.reduce(
        (inError, operation) => (operation.error ? inError + 1 : inError),
        0
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(submitFormSuccess, () => initialState);
  },
});

export default attachmentsSlice.reducer;
const { actions } = attachmentsSlice;

export const addAttachments =
  (fieldName: string, fileNames: string[]): AppThunk =>
  async (dispatch) => {
    fileNames.forEach((fileName) => {
      dispatch(actions.addAtachment({ fieldName, fileName }));
    });
  };

export const replaceAttachments =
  (fieldName: string, fileNames: string[]): AppThunk =>
  async (dispatch) => {
    fileNames.forEach((fileName) => {
      dispatch(actions.replaceAtachment({ fieldName, fileName }));
    });
  };

export const removeAttachment =
  (fieldName: string, fileName: string): AppThunk =>
  async (dispatch) => {
    dispatch(actions.removeAttachment({ fieldName, fileName }));
  };
export const retryOperation =
  (fieldName: string, fileName: string): AppThunk =>
  async (dispatch) => {
    dispatch(actions.retryOperation({ fieldName, fileName }));
  };

export const cancelOperation =
  (fieldName: string, fileName: string): AppThunk =>
  async (dispatch) => {
    dispatch(actions.cancelOperation({ fieldName, fileName }));
  };

export const purgeOperations =
  (fieldName: string, fileNames: string[]): AppThunk =>
  async (dispatch) => {
    dispatch(actions.purgeOperations({ fieldName, fileNames }));
  };

export const submitAll =
  (
    fieldName: string,
    operationFilter: "remove" | undefined,
    uploadFiles: File[]
  ): AppThunk =>
  (dispatch, getState) => {
    const submitSuccessHandler = (fileName: string) => () => {
      dispatch(
        actions.submitSuccess({
          fieldName,
          fileName,
        })
      );
      return { fileName };
    };
    const submitErrorHandler = (fileName: string) => (err: any) => {
      dispatch(
        actions.submitFailed({
          fieldName,
          fileName,
          error: err.toString(),
        })
      );
      return { fileName, error: err };
    };

    const promises: Promise<{ fileName: string; error?: any }>[] = [];

    const pendingOperations = getState().attachments.items[
      fieldName
    ]?.submitOperations.filter(
      (x) =>
        x.status === "new" &&
        (!operationFilter || x.operation === operationFilter)
    );
    pendingOperations?.forEach((pendingOperation) => {
      const { fileName, operation } = pendingOperation;

      switch (operation) {
        case "upload":
        case "replace": {
          const file = uploadFiles.find((x) => x.name === fileName);
          if (!file)
            throw new Error(
              `upload pending operation for file name '${fileName}' does not contain File to upload`
            );
          dispatch(actions.submitStart({ fieldName, fileName }));
          const promise = performPostAttachment(file, (progress) =>
            dispatch(actions.updateProgress({ fieldName, fileName, progress }))
          ).then(submitSuccessHandler(fileName), submitErrorHandler(fileName));

          promises.push(promise);
          break;
        }
      }
    });
    return Promise.allSettled(promises);
  };

// export const submitPendingUploads = (
//   fieldName: string,
//   files: File[]
// ): AppThunk => (dispatch, getState) => {
//   const submitSuccessHandler = (fileName: string) => () => {
//     dispatch(
//       actions.submitSuccess({
//         fieldName,
//         fileName,
//       })
//     );
//     return { fileName };
//   };
//   const submitErrorHandler = (fileName: string) => (err: any) => {
//     dispatch(
//       actions.submitFailed({
//         fieldName,
//         fileName,
//         error: err.toString(),
//       })
//     );
//     return { fileName, error: err };
//   };

//   const formKey = getState().formData.formKey;
//   const promises: Promise<{ fileName: string; error?: any }>[] = [];

//   files?.forEach((file) => {
//     const pendingOperation = getState().attachments.items[
//       fieldName
//     ]?.submitOperations.find(
//       (x) => x.status === "new" && x.fileName === file.name
//     );
//     if (!pendingOperation) return;

//     const { fileName, operation } = pendingOperation;

//     dispatch(actions.submitStart({ fieldName, fileName }));

//     if (operation === "upload" || operation === "replace") {
//       if (!file)
//         throw new Error(
//           `upload pending operation for file name '${fileName}' does not contain File to upload`
//         );
//       const promise = performPostAttachment(
//         formKey,
//         fieldName,
//         file,
//         (progress) =>
//           dispatch(actions.updateProgress({ fieldName, fileName, progress }))
//       ).then(submitSuccessHandler(fileName), submitErrorHandler(fileName));

//       promises.push(promise);
//     } else {
//       throw new Error(
//         `pending operation for file name '${fileName}' is '${operation}'. you can't submit upload for it`
//       );
//     }
//   });
//   return Promise.allSettled(promises);
// };
